import { LitElement, html } from '@hh/global.js'

class SearchLandingFilters extends LitElement {
  static properties = {
    open: {
      type: Boolean,
    },
  }

  constructor() {
    super()
    this._observer = null
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleToggleFilterDrawer()
    this.observeTagalysUpdates()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this._observer) {
      this._observer.disconnect()
      this._observer = null
    }
  }

  /* -----------------------------
   Tagalys DOM observer
  ------------------------------ */
  observeTagalysUpdates() {
    const target = this.closest('.search-landing')
    if (!target) return

    this._observer = new MutationObserver(() => {
      this.handleTooltips()
    })

    this._observer.observe(target, {
      childList: true,
      subtree: true,
    })
  }

  /* -----------------------------
   Tooltip helpers
  ------------------------------ */
  hideAllTooltips() {
    const tooltips = document.querySelectorAll(
      '[data-filter-option-tooltip-content-mobile], [data-filter-option-tooltip-content-desktop]'
    )

    tooltips.forEach((tooltip) => {
      if (tooltip._originalParent) {
        tooltip._originalParent.appendChild(tooltip)
        tooltip._originalParent = null
      }
      tooltip.style.display = 'none'
    })
  }

  showTooltip({ icon, tooltipContent, device, filterItem, event }) {
    tooltipContent._originalParent = filterItem
    document.body.appendChild(tooltipContent)
    tooltipContent.style.display = 'block'

    const isTopLevel = icon.hasAttribute('data-filter-option-toplevel-tooltip')

    if (isTopLevel && device === 'mobile') {
      event.preventDefault()
    }

    if (device === 'desktop') {
      const rect = isTopLevel
        ? icon.getBoundingClientRect()
        : filterItem.getBoundingClientRect()

      tooltipContent.style.left = `${rect.right}px`
      tooltipContent.style.top = `${
        rect.top + rect.height / 2 - tooltipContent.offsetHeight / 2
      }px`
    }
  }

  handleTooltips() {
    const root = this.closest('.search-landing')
    if (!root) return

    const desktopIcons = root.querySelectorAll(
      '[data-filter-option-tooltip-icon-desktop]:not([data-tooltip-bound])'
    )

    const mobileIcons = root.querySelectorAll(
      '[data-filter-option-tooltip-icon-mobile]:not([data-tooltip-bound])'
    )

    // Desktop tooltips
    desktopIcons.forEach((icon) => {
      icon.dataset.tooltipBound = 'true'

      const filterItem = icon.closest('[data-tooltip-parent]')
      const tooltipContent = filterItem?.querySelector(
        '[data-filter-option-tooltip-content-desktop]'
      )
      if (!tooltipContent) return

      const hide = () => {
        if (tooltipContent.parentNode === document.body) {
          filterItem.appendChild(tooltipContent)
        }
        tooltipContent.style.display = 'none'
      }

      icon.addEventListener('mouseenter', (e) =>
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'desktop',
          filterItem,
          event: e,
        })
      )

      icon.addEventListener('focus', (e) =>
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'desktop',
          filterItem,
          event: e,
        })
      )

      icon.addEventListener('mouseleave', hide)
      icon.addEventListener('blur', hide)

      tooltipContent.addEventListener('mouseenter', () => {
        tooltipContent.style.display = 'block'
      })

      tooltipContent.addEventListener('mouseleave', hide)
    })

    // Mobile tooltips
    mobileIcons.forEach((icon) => {
      icon.dataset.tooltipBound = 'true'

      const filterItem = icon.closest('[data-tooltip-parent]')
      const tooltipContent = filterItem?.querySelector(
        '[data-filter-option-tooltip-content-mobile]'
      )
      if (!tooltipContent) return

      icon.addEventListener('click', (e) =>
        this.showTooltip({
          icon,
          tooltipContent,
          device: 'mobile',
          filterItem,
          event: e,
        })
      )
    })
  }

  handleToggleFilterDrawer() {
    const buttonEls = this.querySelectorAll('.js-toggle-filter-button')
    const sectionEl = this.closest('.search-landing')
    const searchLandingFilterDrawer = sectionEl.querySelector(
      'search-landing-filter-drawer'
    )

    buttonEls?.forEach((el) => {
      el.addEventListener('click', () => {
        searchLandingFilterDrawer.toggleAttribute('open')
      })
    })
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('search-landing-filters', SearchLandingFilters)

class SearchLandingFilterDrawer extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.handleRefreshState()
    this.handleCloseTrigger()
    this.handleEscKey()
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated() {
    const sectionEl = this.closest('.search-landing')

    const toggleButton = this.closest('.search-landing').querySelectorAll(
      '.js-toggle-filter-button'
    )

    if (this.open) {
      toggleButton?.forEach((el) => {
        el.classList.add('is-show')
      })

      sectionEl.setAttribute('data-animate-switch', '')
      setTimeout(() => {
        sectionEl.removeAttribute('data-animate-switch')
        sectionEl.classList.add('search-filter-drawer-open')
      }, 500)
    } else {
      toggleButton?.forEach((el) => {
        el.classList.remove('is-show')
      })

      sectionEl.setAttribute('data-animate-switch', '')
      setTimeout(() => {
        sectionEl.removeAttribute('data-animate-switch')
        sectionEl.classList.remove('search-filter-drawer-open')
      }, 500)
    }
  }

  handleRefreshState() {
    const sectionEl = this.closest('.search-landing')
    const isFilterDrawerOpen = sectionEl.classList.contains(
      'search-filter-drawer-open'
    )

    if (!isFilterDrawerOpen) return
    this.setAttribute('open', '')
  }

  closeDrawer() {
    this.removeAttribute('open')
  }

  handleCloseTrigger() {
    const filterDrawerClose = this.querySelectorAll(
      '.js-search-landing-filter-drawer-close'
    )

    filterDrawerClose?.forEach((el) => {
      el.addEventListener('click', () => {
        this.closeDrawer()
      })
    })
  }

  handleEscKey() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeDrawer()
      }
    })
  }

  render() {
    return html`<slot></slot>`
  }
}

customElements.define('search-landing-filter-drawer', SearchLandingFilterDrawer)
